import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as VaultIcon } from "../../assets/icons/vault_icon.svg";
import { ReactComponent as EventsIcon } from "../../assets/icons/events_icon.svg";
import { ReactComponent as ClubsIcon } from "../../assets/icons/clubs_icon.svg";
import { useTranslation } from "react-i18next";
import ChatInputBar from "../menu/ChatInputBar";
import { useMailbox } from "../../context/MailboxContext";
import { useChatUnread } from "../../context/ChatUnreadContext";
import { useStatisticsUnread } from "../../context/StatisticsUnreadContext";
import { useWaitlistUnread } from "../../context/WaitlistUnreadContext";
import { useParticipantRequestUnread } from "../../context/ParticipantRequestUnreadContext";
import { useHostFeedbackUnread } from "../../context/HostFeedbackUnreadContext";
import { useClubMemberUnread } from "../../context/ClubMemberUnreadContext";
import { useClubRequestUnread } from "../../context/ClubRequestUnreadContext";
import { useMutualUnread } from "../../context/MutualUnreadContext";
import { RequiredActionsContext } from "../../context/RequiredActionsContext";
import { useAppState } from "../../context/AppStateContext";
import { countUnreadSinglesNotifications } from "../../utils/singlesNotifications";
import { getChatsAttentionCount } from "../../utils/attentionBadges";
import {
  buildEventsNavigationState,
  getHostEventAttentionTotal,
} from "../../utils/eventHostAttention";
import FloatingParticipantRulesButton from "../events/FloatingParticipantRulesButton";
import FloatingSubscribeButton from "../menu/FloatingSubscribeButton";
import { isChatInputPath } from "../layout/appShellLayout";
import { clearChatInputSlotSnapshot, getChatInputSlotSnapshot } from "./chatInputSlotSnapshot";
import {
  CHAT_KEYBOARD_BOTTOM_GAP_PX,
  CHAT_KEYBOARD_TRANSITION,
  buildChatInputViewportStyle,
  useChatVisualViewport,
} from "../../hooks/useDirectChatVisualViewport";
import { isNativeApp, isNativeIOS } from "../../native/runtime";


// Bottom navigation icon size (px)
const NAV_ICON_SIZE = 20;
const CLUBS_NAV_ICON_SIZE = 22;
const MENU_ASSET_BASE = "/assets/menu";
const CLUBS_NAV_WORDMARK_SRC = "/assets/clubs/clubs_nav_wordmark.svg";
const FIGMA_NAV_ASSETS = {
  moreRing: `${MENU_ASSET_BASE}/bottom_nav_more_ring.svg`,
  chatsIcon: `${MENU_ASSET_BASE}/chats_icon.svg`,
  compassIcon: `${MENU_ASSET_BASE}/compas_icon.svg?v=20260922`,
  closeIcon: `${MENU_ASSET_BASE}/x_icon.svg`,
};
// Badge position tuning (px). Adjust these to nudge badges left/right/up/down.
// NOTE: Negative X moves left, positive X moves right.
const BADGE_OFFSETS = {
  // Unread mutual-connections badge on the Vault tab
  vault: { x: -7, y: 5 },
  // Unread waitlist activity badge on the Events tab
  events: { x: -7, y: 5 },
  // Unread statistics badge on the Search tab
  search: { x: -7, y: 5 },
  // Unread chats badge on the Menu tab
  menu: { x: -7, y: 5 },
  // Aggregated unread badge on the collapsed Compass tab
  compass: { x: -7, y: 5 },
  // Reserved for future tab badge offsets
  clubs: { x: -7, y: 5 },
};

const NAV_MIN_VISIBLE_HEIGHT_PX = 72;
const NAV_EXTRA_CLEARANCE_PX = 32;
const NAV_MIN_CLEARANCE_PX = 112;
const NAV_BOTTOM_GAP_PX = CHAT_KEYBOARD_BOTTOM_GAP_PX;
const NAV_MORE_PANEL_TRANSITION_MS = 200;
const NAV_FLOATING_OVERLAY_GAP_PX = 20;
const NAV_BASE_WIDTH_PX = 340;
const NAV_HEIGHT_PX = 70;
const NAV_MIN_PAGE_GUTTER_PX = 24;
const NAV_MAX_PAGE_GUTTER_PX = 31;
const NAV_PAGE_GUTTER_VW = 0.075;
const NAV_MAX_WIDTH_PX = 430;
const NAV_WIDTH_CSS = "var(--vg-bottom-nav-width, min(calc(100vw - 48px), 430px))";
const TOAST_NO_NAV_CLEARANCE = "max(env(safe-area-inset-bottom, 0px), 22px)";
const PAGE_OVERLAY_SELECTOR = "[data-bottom-clearance-overlay]";
const NAV_FLOATING_OVERLAY_SELECTORS = [
  "#bottom-nav-more-panel",
  "#participant-rating-rules-fab",
  "#singles-discover-actions",
  "#subscription-fab",
];
const NAV_SECTION_SURFACE_STYLE = {
  backgroundColor: "rgba(53, 53, 53, 0.5)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  boxShadow:
    "inset 0 0 0 0.5px rgba(255,255,255,0.24), inset 0 0 0 1.5px rgba(255,255,255,0.08)",
};
const NAV_SECTION_COMPOSITOR_SAFE_STYLE = {
  ...NAV_SECTION_SURFACE_STYLE,
  backgroundColor: "rgba(43, 43, 43, 0.94)",
  backdropFilter: "none",
  WebkitBackdropFilter: "none",
};
const SINGLES_DISCOVER_DISLIKE_SRC = "/assets/singles/dislike.svg";
const SINGLES_DISCOVER_BIG_HEART_SRC = "/assets/singles/big_heart_icon.svg";
const SINGLES_DISCOVER_STAR_SRC = "/assets/singles/star.svg";
const SINGLES_DISCOVER_HEART_OFFSET_Y_REM = 0.97;
const SINGLES_NAV_NOTIFICATION_KINDS = [
  "singles_new_profile",
  "singles_match",
  "singles_superlike",
];
const DINNERS_NAV_NOTIFICATION_KINDS = [
  "dinner_vote_open",
  "dinner_vote_reminder",
  "dinner_vote_timeout_removed",
  "dinner_attendance_reminder",
];
const DINNERS_REQUIRED_ACTION_TYPES = new Set([
  "dinner_date_vote",
  "dinner_table_proposal",
  "dinner_final_confirmation",
]);

function getNotificationActionKind(notification) {
  const rawMeta = notification?.meta;
  let meta = {};

  if (rawMeta && typeof rawMeta === "string") {
    try {
      const parsed = JSON.parse(rawMeta);
      meta = parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      meta = {};
    }
  } else if (rawMeta && typeof rawMeta === "object") {
    meta = rawMeta;
  }

  return String(meta.action_kind || meta.actionKind || "").trim().toLowerCase();
}

function countUnreadMailboxNotifications(notifications, allowedKinds) {
  const allowed = new Set(
    allowedKinds.map((kind) => String(kind || "").trim().toLowerCase()).filter(Boolean)
  );

  return (Array.isArray(notifications) ? notifications : []).reduce((count, notification) => {
    if (!notification || notification.is_read) return count;
    return allowed.has(getNotificationActionKind(notification)) ? count + 1 : count;
  }, 0);
}

function SinglesDiscoverActionButton({
  id,
  onClick,
  disabled = false,
  active = false,
  large = false,
  children,
}) {
  const baseClass = active
    ? "border-transparent bg-transparent shadow-none"
    : "border-[#101827]/85 bg-white text-[#1E2741]";

  return (
    <button
      id={id}
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative inline-flex shrink-0 items-center justify-center rounded-full border transition ${
        large
          ? "h-[7.2rem] w-[7.2rem]"
          : "h-[4.6rem] w-[4.6rem] shadow-[0_20px_50px_rgba(0,0,0,0.07)]"
      } ${baseClass} ${disabled ? "cursor-not-allowed opacity-55" : ""}`}
    >
      <span className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
        {children}
      </span>
    </button>
  );
}

function SinglesDiscoverSuperLikeCounter({ remaining = 0, total = 5 }) {
  const positions = [
    { top: "0.05rem", left: "50%" },
    { top: "0.5rem", left: "26%" },
    { top: "0.5rem", left: "74%" },
    { top: "1.45rem", left: "10%" },
    { top: "1.45rem", left: "90%" },
  ];

  return (
    <div className="pointer-events-none absolute inset-0">
      {positions.slice(0, total).map((position, index) => (
        <span
          key={`${position.left}-${position.top}`}
          className={`absolute h-[0.48rem] w-[0.48rem] rounded-full border border-white/30 ${
            index < remaining
              ? "bg-[linear-gradient(135deg,#C86AF6,#7E36CF)] shadow-[0_0_18px_rgba(174,74,224,0.35)]"
              : "bg-white/[0.08]"
          }`}
          style={{
            top: position.top,
            left: position.left,
            transform: "translate(-50%, -50%)",
          }}
        />
      ))}
    </div>
  );
}

function PlusNavIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path d="M12 4.75V19.25M4.75 12H19.25" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

const BottomNavigation = ({ className = "", isGuest = false, showScrim = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "vault"]);
  const usesAnimatedBackground =
    location.pathname.startsWith("/menu/singles") ||
    location.pathname.startsWith("/menu/dinners");
  const navSectionSurfaceStyle = usesAnimatedBackground
    ? NAV_SECTION_COMPOSITOR_SAFE_STYLE
    : NAV_SECTION_SURFACE_STYLE;
  const navRef = useRef(null);
  const chatInputPathActive = isChatInputPath(location.pathname);
  const chatInputPathActiveRef = useRef(chatInputPathActive);
  chatInputPathActiveRef.current = chatInputPathActive;
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerWidth
  );
  const navMetricsRef = useRef({
    visibleHeight: NAV_MIN_VISIBLE_HEIGHT_PX,
    boxHeight: NAV_MIN_VISIBLE_HEIGHT_PX,
    overlayClearance: NAV_MIN_CLEARANCE_PX,
    clearance: NAV_MIN_CLEARANCE_PX,
  });
  const shrinkTimerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateViewportWidth = () => {
      const nextWidth = Math.round(window.visualViewport?.width || window.innerWidth || 0);
      setViewportWidth(nextWidth);
    };

    updateViewportWidth();
    window.addEventListener("resize", updateViewportWidth, { passive: true });
    window.visualViewport?.addEventListener("resize", updateViewportWidth);
    return () => {
      window.removeEventListener("resize", updateViewportWidth);
      window.visualViewport?.removeEventListener("resize", updateViewportWidth);
    };
  }, []);

  // Unread chats badge (sum across internal chats)
  // IMPORTANT: hooks must be called unconditionally on every render
  const chatUnreadCtx = useChatUnread();
  const unreadTotalRaw =
    (chatUnreadCtx?.total_unread ??
      chatUnreadCtx?.totalUnread ??
      chatUnreadCtx?.unread_total ??
      chatUnreadCtx?.unreadTotal ??
      chatUnreadCtx?.total ??
      0);
  const unreadTotal = isGuest ? 0 : (Number(unreadTotalRaw) || 0);

  // Unread statistics badge (ActivityLog)
  // IMPORTANT: hooks must be called unconditionally on every render
  const statisticsUnreadCtx = useStatisticsUnread();
  const unreadStatisticsRaw =
    (statisticsUnreadCtx?.unreadStatistics ??
      statisticsUnreadCtx?.unread_statistics ??
      statisticsUnreadCtx?.total_unread ??
      statisticsUnreadCtx?.totalUnread ??
      0);
  const unreadStatistics = isGuest ? 0 : (Number(unreadStatisticsRaw) || 0);

  // Unread waitlist joins on host-created events
  const waitlistUnreadCtx = useWaitlistUnread();
  const unreadWaitlistRaw =
    (waitlistUnreadCtx?.totalUnread ??
      waitlistUnreadCtx?.total_unread ??
      waitlistUnreadCtx?.unreadTotal ??
      waitlistUnreadCtx?.unread_total ??
      0);
  const unreadWaitlist = isGuest ? 0 : (Number(unreadWaitlistRaw) || 0);
  const participantRequestUnreadCtx = useParticipantRequestUnread();
  const unreadParticipantRequestsRaw =
    (participantRequestUnreadCtx?.totalUnread ??
      participantRequestUnreadCtx?.total_unread ??
      participantRequestUnreadCtx?.unreadTotal ??
      participantRequestUnreadCtx?.unread_total ??
      0);
  const unreadParticipantRequests = isGuest ? 0 : (Number(unreadParticipantRequestsRaw) || 0);
  const hostFeedbackUnreadCtx = useHostFeedbackUnread();
  const unreadHostFeedback = isGuest
    ? 0
    : (Number(hostFeedbackUnreadCtx?.totalUnread || 0) || 0);
  const hostEventAttentionTotal = getHostEventAttentionTotal(
    unreadWaitlist,
    unreadParticipantRequests,
    unreadHostFeedback
  );
  const eventsNavigationState = buildEventsNavigationState(
    unreadWaitlist,
    unreadParticipantRequests,
    unreadHostFeedback
  );
  const clubMemberUnreadCtx = useClubMemberUnread();
  const unreadClubMembers = isGuest ? 0 : (Number(clubMemberUnreadCtx?.totalUnread || 0) || 0);
  const unreadJoinedClubs = isGuest
    ? 0
    : (Number(clubMemberUnreadCtx?.joinedTotalUnread || 0) || 0);
  const clubRequestUnreadCtx = useClubRequestUnread();
  const pendingClubRequests = isGuest ? 0 : (Number(clubRequestUnreadCtx?.totalPending || 0) || 0);

  const mutualUnreadCtx = useMutualUnread();
  const unreadMutualRaw =
    (mutualUnreadCtx?.unreadCount ??
      mutualUnreadCtx?.unread_count ??
      mutualUnreadCtx?.total_unread ??
      0);
  const unreadMutual = isGuest ? 0 : (Number(unreadMutualRaw) || 0);
  const mailboxCtx = useMailbox();
  const requiredActionsCtx = React.useContext(RequiredActionsContext);
  const unreadMailbox = isGuest
    ? 0
    : (Number(mailboxCtx?.unreadCount ?? 0) || (Array.isArray(mailboxCtx?.notifications)
      ? mailboxCtx.notifications.length
      : 0));
  const pendingRequiredActionsCount = isGuest || !Array.isArray(requiredActionsCtx?.actions)
    ? 0
    : requiredActionsCtx.actions.length;
  const chatsAttentionTotal = getChatsAttentionCount(
    unreadTotal,
    unreadMailbox,
    pendingRequiredActionsCount
  );
  const unreadSingles = React.useMemo(
    () =>
      isGuest
        ? 0
        : countUnreadSinglesNotifications(
            mailboxCtx?.notifications,
            SINGLES_NAV_NOTIFICATION_KINDS
          ),
    [isGuest, mailboxCtx?.notifications]
  );
  const unreadDinnersMailbox = React.useMemo(
    () =>
      isGuest
        ? 0
        : countUnreadMailboxNotifications(
            mailboxCtx?.notifications,
            DINNERS_NAV_NOTIFICATION_KINDS
          ),
    [isGuest, mailboxCtx?.notifications]
  );
  const unreadDinnersRequiredActions = React.useMemo(() => {
    if (isGuest) return 0;
    const actions = Array.isArray(requiredActionsCtx?.actions) ? requiredActionsCtx.actions : [];
    return actions.reduce((count, action) => (
      DINNERS_REQUIRED_ACTION_TYPES.has(String(action?.type || "").trim())
        ? count + 1
        : count
    ), 0);
  }, [isGuest, requiredActionsCtx?.actions]);
  const unreadDinners = Math.max(unreadDinnersMailbox, unreadDinnersRequiredActions);
  const unreadCompass = isGuest ? 0 : unreadSingles + unreadDinners + unreadStatistics;
  const { hasFullVaultCoins } = useAppState();

  const guardClick = (e, name) => {
    // If user navigates away using bottom tabs, ensure chat input slot is closed
    setChatInputSlot({ visible: false, props: null });
    chatSlotVisibleRef.current = false;
    setIsMoreOpen(false);

    if (isGuest && name !== "events" && name !== "clubs") {
      e.preventDefault();
      navigate("/auth", { replace: true });
    }
  };

  const collapseTimerRef = useRef(null);
  const morePanelTimerRef = useRef(null);
  const morePanelRafRef = useRef(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isMorePanelRendered, setIsMorePanelRendered] = useState(false);
  const [isMorePanelVisible, setIsMorePanelVisible] = useState(false);
  const [bottomNavSuppressionSources, setBottomNavSuppressionSources] = useState(() => new Set());
  const bottomNavSuppressed = bottomNavSuppressionSources.size > 0;

  // Chat input slot (render ChatInputBar inside BottomNavigation when chat is open)
  // Expected events:
  // - window.dispatchEvent(new CustomEvent("vg-chat-input-open", { detail: { props: { ... } } }))
  // - window.dispatchEvent(new CustomEvent("vg-chat-input-close"))
  const [chatInputSlot, setChatInputSlot] = useState(() =>
    getChatInputSlotSnapshot(location.pathname)
  );
  // Tracks whether chat input slot is currently visible (avoids re-expanding nav on every props update)
  const chatSlotVisibleRef = useRef(chatInputSlot.visible);
  const [participantRatingScreen, setParticipantRatingScreen] = useState({
    screen: null,
    rulesAvailable: false,
  });
  const [singlesDiscoverActionsState, setSinglesDiscoverActionsState] = useState({
    visible: false,
    swipeDisabled: true,
    superLikeDisabled: true,
    showSuperLikeCounter: false,
    superLikesRemaining: 0,
  });

  const startCollapseTimer = () => {
    clearTimeout(collapseTimerRef.current);
  };

  useEffect(() => () => {
    clearTimeout(collapseTimerRef.current);
    clearTimeout(morePanelTimerRef.current);
    if (morePanelRafRef.current != null) {
      window.cancelAnimationFrame(morePanelRafRef.current);
    }
  }, []);

  useEffect(() => {
    window.clearTimeout(morePanelTimerRef.current);
    if (morePanelRafRef.current != null) {
      window.cancelAnimationFrame(morePanelRafRef.current);
      morePanelRafRef.current = null;
    }

    if (isMoreOpen) {
      setIsMorePanelRendered(true);
      morePanelRafRef.current = window.requestAnimationFrame(() => {
        morePanelRafRef.current = null;
        setIsMorePanelVisible(true);
      });
      return () => {
        if (morePanelRafRef.current != null) {
          window.cancelAnimationFrame(morePanelRafRef.current);
          morePanelRafRef.current = null;
        }
      };
    }

    setIsMorePanelVisible(false);
    morePanelTimerRef.current = window.setTimeout(
      () => setIsMorePanelRendered(false),
      NAV_MORE_PANEL_TRANSITION_MS
    );
    return () => window.clearTimeout(morePanelTimerRef.current);
  }, [isMoreOpen]);

  useEffect(() => {
    const suppressionHandler = (event) => {
      const detail = event?.detail || {};
      const source = String(detail.source || "default");
      const active = Boolean(detail.active);

      setBottomNavSuppressionSources((prev) => {
        const next = new Set(prev);
        if (active) next.add(source);
        else next.delete(source);
        return next;
      });
    };

    window.addEventListener("vg-bottom-nav-suppression", suppressionHandler);
    return () => {
      window.removeEventListener("vg-bottom-nav-suppression", suppressionHandler);
    };
  }, []);

  useEffect(() => {
    if (!bottomNavSuppressed) return;
    setIsMoreOpen(false);
    if (chatSlotVisibleRef.current) {
      setChatInputSlot({ visible: false, props: null });
      chatSlotVisibleRef.current = false;
    }
  }, [bottomNavSuppressed]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleParticipantRatingScreen = (event) => {
      const detail = event?.detail || {};
      setParticipantRatingScreen({
        screen: detail.screen || null,
        rulesAvailable: Boolean(detail.rulesAvailable),
      });
    };

    window.addEventListener("vg-participant-rating-screen", handleParticipantRatingScreen);
    return () => {
      window.removeEventListener("vg-participant-rating-screen", handleParticipantRatingScreen);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handler = (event) => {
      const detail = event?.detail || {};
      setSinglesDiscoverActionsState({
        visible: Boolean(detail.visible),
        swipeDisabled: detail.swipeDisabled !== false,
        superLikeDisabled: detail.superLikeDisabled !== false,
        showSuperLikeCounter: Boolean(detail.showSuperLikeCounter),
        superLikesRemaining: Number(detail.superLikesRemaining || 0) || 0,
      });
    };

    window.addEventListener("vg-singles-discover-actions-state", handler);
    return () => window.removeEventListener("vg-singles-discover-actions-state", handler);
  }, []);

  // Listen chat input slot open/close (ChatInputBar rendered inside BottomNavigation)
  useEffect(() => {
    const openHandler = (e) => {
      // The slot is global because it lives in BottomNavigation. Never let a
      // delayed chat effect replace the regular nav after route navigation.
      if (!chatInputPathActiveRef.current) return;

      const detail = e?.detail || {};
      const props = detail?.props || null;

      setChatInputSlot(() => {
        chatSlotVisibleRef.current = true;
        return { visible: true, props };
      });

      startCollapseTimer();
    };

    const updateHandler = (e) => {
      if (!chatInputPathActiveRef.current) return;

      // Update props without re-expanding / re-triggering collapse logic.
      // This prevents focus/keyboard glitches caused by re-mounting/re-opening the slot.
      const detail = e?.detail || {};
      const props = detail?.props || null;

      // If slot isn't visible, ignore updates.
      if (!chatSlotVisibleRef.current) return;

      setChatInputSlot((prev) => {
        if (!prev?.visible) return prev;
        return { ...prev, props };
      });
    };

    const closeHandler = () => {
      setChatInputSlot(() => {
        chatSlotVisibleRef.current = false;
        return { visible: false, props: null };
      });
      chatSlotVisibleRef.current = false;

      // When chat input closes, go back to normal collapse timing
      startCollapseTimer();
    };

    window.addEventListener("vg-chat-input-open", openHandler);
    window.addEventListener("vg-chat-input-close", closeHandler);
    window.addEventListener("vg-chat-input-update", updateHandler);

    // The chat can dispatch its first open before this component's effects run.
    // The module-level snapshot also covers a later BottomNavigation remount.
    const current = getChatInputSlotSnapshot(window.location.pathname);
    if (chatInputPathActiveRef.current && current.visible) {
      chatSlotVisibleRef.current = true;
      setChatInputSlot(current);
    }

    return () => {
      window.removeEventListener("vg-chat-input-open", openHandler);
      window.removeEventListener("vg-chat-input-close", closeHandler);
      window.removeEventListener("vg-chat-input-update", updateHandler);
    };
  }, []);

  useEffect(() => {
    if (isChatInputPath(location.pathname)) {
      const current = getChatInputSlotSnapshot(location.pathname);
      chatSlotVisibleRef.current = current.visible;
      setChatInputSlot((previous) =>
        current.visible || previous.visible ? current : previous
      );
      return;
    }

    clearChatInputSlotSnapshot();
    chatSlotVisibleRef.current = false;
    setChatInputSlot((prev) =>
      prev.visible || prev.props ? { visible: false, props: null } : prev
    );

    // Chat overlays own these suppression sources. If navigation wins a race
    // with their cleanup, they must not keep the regular nav hidden either.
    setBottomNavSuppressionSources((prev) => {
      if (!prev.has("event-chat-overlay") && !prev.has("direct-chat-overlay")) {
        return prev;
      }
      const next = new Set(prev);
      next.delete("event-chat-overlay");
      next.delete("direct-chat-overlay");
      return next;
    });

    clearTimeout(collapseTimerRef.current);
  }, [location.pathname]);

  const activeTab = (() => {
    if (location.pathname.startsWith("/clubs") || location.pathname.startsWith("/menu/clubs")) return "clubs";
    if (location.pathname.startsWith("/shop")) return "shop";
    if (location.pathname.startsWith("/menu/singles")) return "singles";
    if (location.pathname.startsWith("/menu/dinners")) return "dinners";
    if (location.pathname.startsWith("/search")) return "search";
    if (location.pathname.startsWith("/vault")) return "vault";
    if (location.pathname.startsWith("/events")) return "events";
    if (location.pathname.startsWith("/menu/chats")) return "chats";
    if (location.pathname.startsWith("/menu")) return "menu";
    return "";
  })();
  const moreTabNames = React.useMemo(
    () => new Set(["shop", "singles", "dinners", "search"]),
    []
  );
  const isMoreTabActive = moreTabNames.has(activeTab);
  const isChatParticipantsView = React.useMemo(() => {
    if (!location.pathname.startsWith("/menu/chats/")) return false;
    try {
      const qs = new URLSearchParams(location.search || "");
      return qs.get("participants") === "1";
    } catch {
      return false;
    }
  }, [location.pathname, location.search]);
  const showChatInputSlot =
    chatInputPathActive &&
    !bottomNavSuppressed &&
    chatInputSlot.visible &&
    !isChatParticipantsView &&
    (!isGuest || chatInputSlot?.props?.allowGuest);
  const layoutViewportCoordinates = isNativeApp();
  const chatVisualViewport = useChatVisualViewport(!showChatInputSlot, {
    trackOpeningAnimation: layoutViewportCoordinates,
  });
  const chatInputViewportStyle = buildChatInputViewportStyle(
    chatVisualViewport,
    NAV_BOTTOM_GAP_PX,
    { layoutViewportCoordinates }
  );
  const nativeIosChatOverlay = showChatInputSlot && isNativeIOS();
  const nativeIosChatRestingStyle = nativeIosChatOverlay
    ? {
        bottom: "0px",
        top: "auto",
        transform:
          `translate3d(0, calc(0px - max(env(safe-area-inset-bottom, 0px), ${NAV_BOTTOM_GAP_PX}px)), 0)`,
        transition: `transform ${CHAT_KEYBOARD_TRANSITION}`,
        willChange: "transform",
      }
    : null;
  const showParticipantRulesButton =
    Boolean(participantRatingScreen.rulesAvailable) &&
    participantRatingScreen.screen === "main" &&
    !bottomNavSuppressed &&
    !showChatInputSlot &&
    !isMoreOpen;
  const showSubscriptionButton =
    location.pathname === "/menu/subscription" &&
    !bottomNavSuppressed &&
    !showChatInputSlot &&
    !isMoreOpen;

  useEffect(() => {
    if (!isChatParticipantsView) return;

    setChatInputSlot({ visible: false, props: null });
    chatSlotVisibleRef.current = false;
    startCollapseTimer();
  }, [isChatParticipantsView]);

  const renderUnreadBadge = (count, offsets = BADGE_OFFSETS.menu) => {
    const n = Number(count) || 0;
    if (n <= 0) return null;

    const formatBadgeCount = (value) => {
      if (value < 100) return String(value);
      if (value < 1000) return "99+";
      if (value < 1000000) {
        const compact = value < 10000 ? Math.floor(value / 100) / 10 : Math.floor(value / 1000);
        return `${String(compact).replace(/\.0$/, "")}K`;
      }
      const compact = value < 10000000 ? Math.floor(value / 100000) / 10 : Math.floor(value / 1000000);
      return `${String(compact).replace(/\.0$/, "")}M`;
    };
    const label = formatBadgeCount(n);

    // Slightly smaller for 3+ digits to fit near icons
    const isWide = label.length >= 2;
    const isVeryWide = label.length >= 3;

    return (
      <span
        className={
          "absolute right-[4px] top-[10px] z-[4] font-semibold bg-white/90 text-[#1F74FF] " +
          // Sizing (circle for 1 digit; capsule for 2+ digits)
          (isVeryWide
            ? "text-[9px] h-[18px] min-w-[18px] px-[4px] "
            : isWide
            ? "text-[10px] h-[18px] min-w-[18px] px-[4px] "
            : "text-[12px] h-[18px] w-[18px] ") +
          "rounded-full flex items-center justify-center leading-none"
        }
        style={{ transform: `translate(${offsets?.x ?? -7}px, ${offsets?.y ?? 5}px)` }}
        aria-label={`Unread messages: ${label}`}
      >
        {label}
      </span>
    );
  };

  const vaultBadgeCount = isGuest
    ? 0
    : unreadMutual > 0
      ? unreadMutual
      : hasFullVaultCoins
        ? 1
        : 0;

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    const root = document.documentElement;
    const body = document.body;

    const applyMetrics = (target, metrics) => {
      if (!target?.style) return;
      target.style.setProperty("--bottom-nav-visible-height", metrics.height);
      target.style.setProperty("--bottom-nav-height", metrics.boxHeight);
      target.style.setProperty("--bottom-nav-box-height", metrics.boxHeight);
      target.style.setProperty("--bottom-nav-base-clearance", metrics.baseClearance);
      target.style.setProperty("--bottom-nav-overlay-clearance", metrics.overlayClearance);
      target.style.setProperty("--bottom-nav-floating-clearance", metrics.overlayClearance);
      target.style.setProperty("--bottom-nav-present", metrics.navPresent);
      target.style.setProperty("--bottom-page-overlay-clearance", metrics.pageOverlayClearance);
      target.style.setProperty("--bottom-nav-content-clearance", metrics.clearance);
      target.style.setProperty("--bottom-nav-clearance", metrics.clearance);
      target.style.setProperty("--toast-bottom-clearance", metrics.toastClearance);
      target.style.setProperty("--mobile-bottom-clearance", metrics.clearance);
      target.style.setProperty("--nav-h", metrics.boxHeight);
    };

    const getOverlayClearance = (
      selectors,
      viewportTop,
      viewportBottom,
      fallbackClearance,
      defaultGapPx = NAV_FLOATING_OVERLAY_GAP_PX
    ) => {
      let nextClearance = fallbackClearance;

      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          const computed = window.getComputedStyle(node);
          if (computed.display === "none" || computed.visibility === "hidden") return;

          const rect = node.getBoundingClientRect();
          if (!Number.isFinite(rect.top) || !Number.isFinite(rect.bottom)) return;
          if (rect.width <= 0 || rect.height <= 0) return;
          if (rect.bottom <= viewportTop || rect.top >= viewportBottom) return;

          const gapAttr = Number.parseFloat(
            node.getAttribute("data-bottom-clearance-gap") || ""
          );
          const gapPx = Number.isFinite(gapAttr) ? gapAttr : defaultGapPx;

          nextClearance = Math.max(
            nextClearance,
            Math.ceil(viewportBottom - Math.max(rect.top, viewportTop) + gapPx)
          );
        });
      });

      return nextClearance;
    };

    const commitMetrics = (
      visibleHeight,
      boxHeight,
      overlayClearance,
      contentClearance,
      force = false
    ) => {
      const normalizedVisible = Math.max(
        0,
        Math.round(Number.isFinite(visibleHeight) ? visibleHeight : 0)
      );
      const normalizedBox = Math.max(
        normalizedVisible,
        Math.round(Number.isFinite(boxHeight) ? boxHeight : normalizedVisible)
      );

      const effectiveVisible =
        normalizedVisible > 0
          ? Math.max(NAV_MIN_VISIBLE_HEIGHT_PX, normalizedVisible)
          : 0;
      const clearanceBase =
        normalizedBox > 0 ? normalizedBox : effectiveVisible;
      const effectiveClearance =
        effectiveVisible > 0
          ? Math.max(
              NAV_MIN_CLEARANCE_PX,
              clearanceBase + NAV_EXTRA_CLEARANCE_PX
            )
          : 0;
      const effectiveOverlayClearance =
        effectiveVisible > 0
          ? Math.max(
              effectiveClearance,
              Math.round(
                Number.isFinite(overlayClearance) ? overlayClearance : effectiveClearance
              )
            )
          : 0;
      const effectiveContentClearance =
        effectiveVisible > 0
          ? Math.max(
              effectiveOverlayClearance,
              Math.round(
                Number.isFinite(contentClearance) ? contentClearance : effectiveOverlayClearance
              )
            )
          : 0;
      const pageOverlayClearance = Math.max(
        0,
        effectiveContentClearance - effectiveOverlayClearance
      );

      if (
        !force &&
        navMetricsRef.current.visibleHeight === effectiveVisible &&
        navMetricsRef.current.boxHeight === normalizedBox &&
        navMetricsRef.current.overlayClearance === effectiveOverlayClearance &&
        navMetricsRef.current.clearance === effectiveContentClearance
      ) {
        return;
      }

      navMetricsRef.current = {
        visibleHeight: effectiveVisible,
        boxHeight: normalizedBox,
        overlayClearance: effectiveOverlayClearance,
        clearance: effectiveContentClearance,
      };

      const metrics = {
        height: `${effectiveVisible}px`,
        boxHeight: `${normalizedBox}px`,
        baseClearance: `${effectiveClearance}px`,
        overlayClearance: `${effectiveOverlayClearance}px`,
        navPresent: effectiveVisible > 0 ? "1" : "0",
        pageOverlayClearance: `${pageOverlayClearance}px`,
        clearance: `${effectiveContentClearance}px`,
        toastClearance: effectiveVisible > 0
          ? `${effectiveContentClearance}px`
          : TOAST_NO_NAV_CLEARANCE,
      };

      applyMetrics(root, metrics);
      applyMetrics(body, metrics);
    };

    const scheduleShrinkCommit = (nextVisible, nextBox) => {
      window.clearTimeout(shrinkTimerRef.current);
      shrinkTimerRef.current = window.setTimeout(() => {
        const vv = window.visualViewport;
        const viewportTop =
          vv && Number.isFinite(vv.offsetTop) ? vv.offsetTop : 0;
        const viewportHeight =
          vv && Number.isFinite(vv.height) ? vv.height : window.innerHeight;
        const viewportBottom = viewportTop + viewportHeight;
        const measuredBaseClearance =
          nextVisible > 0
            ? Math.max(
                NAV_MIN_CLEARANCE_PX,
                nextBox + NAV_BOTTOM_GAP_PX + NAV_EXTRA_CLEARANCE_PX
              )
            : 0;

        const nextOverlayClearance = getOverlayClearance(
          NAV_FLOATING_OVERLAY_SELECTORS,
          viewportTop,
          viewportBottom,
          measuredBaseClearance
        );
        const nextContentClearance = getOverlayClearance(
          [PAGE_OVERLAY_SELECTOR],
          viewportTop,
          viewportBottom,
          nextOverlayClearance
        );
        commitMetrics(
          nextVisible,
          nextBox,
          nextOverlayClearance,
          nextContentClearance
        );
      }, 280);
    };

    const measure = (force = false) => {
      if (window.innerWidth >= 1024) {
        window.clearTimeout(shrinkTimerRef.current);
        commitMetrics(0, 0, 0, 0, true);
        return;
      }

      const navNode = navRef.current || document.getElementById("bottom-nav");
      if (!navNode) return;

      const vv = window.visualViewport;
      const viewportTop =
        vv && Number.isFinite(vv.offsetTop) ? vv.offsetTop : 0;
      const viewportHeight =
        vv && Number.isFinite(vv.height) ? vv.height : window.innerHeight;
      const viewportBottom = viewportTop + viewportHeight;

      const rect = navNode.getBoundingClientRect();
      const visibleHeight = Math.max(
        0,
        Math.round(
          Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, viewportTop)
        )
      );
      const boxHeight = Math.max(0, Math.round(rect.height));
      const nextVisible = visibleHeight > 0 ? visibleHeight : boxHeight;
      const baseClearance =
        nextVisible > 0
          ? Math.max(
              NAV_MIN_CLEARANCE_PX,
              Math.ceil(viewportBottom - Math.max(rect.top, viewportTop) + NAV_EXTRA_CLEARANCE_PX)
            )
          : 0;
      const nextOverlayClearance = getOverlayClearance(
        NAV_FLOATING_OVERLAY_SELECTORS,
        viewportTop,
        viewportBottom,
        baseClearance
      );
      const nextContentClearance = getOverlayClearance(
        [PAGE_OVERLAY_SELECTOR],
        viewportTop,
        viewportBottom,
        nextOverlayClearance
      );

      if (force || nextVisible >= navMetricsRef.current.visibleHeight) {
        window.clearTimeout(shrinkTimerRef.current);
        commitMetrics(
          nextVisible,
          boxHeight,
          nextOverlayClearance,
          nextContentClearance,
          force
        );
        return;
      }

      scheduleShrinkCommit(nextVisible, boxHeight);
    };

    navMetricsRef.current = {
      visibleHeight: NAV_MIN_VISIBLE_HEIGHT_PX,
      boxHeight: NAV_MIN_VISIBLE_HEIGHT_PX,
      overlayClearance: NAV_MIN_CLEARANCE_PX,
      clearance: NAV_MIN_CLEARANCE_PX,
    };
    commitMetrics(
      NAV_MIN_VISIBLE_HEIGHT_PX,
      NAV_MIN_VISIBLE_HEIGHT_PX,
      NAV_MIN_CLEARANCE_PX,
      NAV_MIN_CLEARANCE_PX,
      true
    );

    let rafId = null;
    const scheduleMeasure = (force = false) => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        measure(force);
      });
    };

    scheduleMeasure(true);
    const timerIds = [
      window.setTimeout(() => scheduleMeasure(true), 80),
      window.setTimeout(() => scheduleMeasure(true), 220),
      window.setTimeout(() => scheduleMeasure(true), 420),
    ];

    const onResize = () => scheduleMeasure();
    const onTransitionEnd = () => scheduleMeasure();
    const navElement = navRef.current;

    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });
    window.visualViewport?.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("scroll", onResize);
    navElement?.addEventListener("transitionend", onTransitionEnd);

    let navResizeObserver = null;
    let overlayResizeObserver = null;
    let overlayMutationObserver = null;
    let overlayReconnectRafId = null;
    const observedOverlayElements = new Set();

    const reconnectOverlayObservers = () => {
      if (!overlayResizeObserver) return;
      observedOverlayElements.forEach((element) => {
        try {
          overlayResizeObserver.unobserve(element);
        } catch {}
      });
      observedOverlayElements.clear();

      [...NAV_FLOATING_OVERLAY_SELECTORS, PAGE_OVERLAY_SELECTOR].forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
          if (!(element instanceof Element)) return;
          observedOverlayElements.add(element);
          try {
            overlayResizeObserver.observe(element);
          } catch {}
        });
      });
    };

    const scheduleOverlayReconnect = () => {
      if (overlayReconnectRafId != null) return;
      overlayReconnectRafId = window.requestAnimationFrame(() => {
        overlayReconnectRafId = null;
        reconnectOverlayObservers();
        scheduleMeasure(true);
      });
    };

    try {
      if (typeof ResizeObserver !== "undefined" && navElement) {
        navResizeObserver = new ResizeObserver(() => scheduleMeasure());
        navResizeObserver.observe(navElement);

        overlayResizeObserver = new ResizeObserver(() => scheduleMeasure(true));
        reconnectOverlayObservers();
      }
      if (typeof MutationObserver !== "undefined" && body) {
        overlayMutationObserver = new MutationObserver(scheduleOverlayReconnect);
        overlayMutationObserver.observe(body, {
          childList: true,
          subtree: true,
        });
      }
    } catch {}

    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      timerIds.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(shrinkTimerRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("scroll", onResize);
      navElement?.removeEventListener("transitionend", onTransitionEnd);
      navResizeObserver?.disconnect?.();
      overlayResizeObserver?.disconnect?.();
      overlayMutationObserver?.disconnect?.();
      if (overlayReconnectRafId != null) {
        window.cancelAnimationFrame(overlayReconnectRafId);
      }
    };
  }, [location.pathname, chatInputSlot.visible, isMoreOpen]);

  useEffect(() => {
    setIsMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMoreOpen) return undefined;
    if (typeof document === "undefined") return undefined;

    const handleOutsidePointerDown = (event) => {
      const navNode = navRef.current;
      if (navNode && navNode.contains(event.target)) return;
      setIsMoreOpen(false);
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
    };
  }, [isMoreOpen]);

  const primaryTabs = [
    {
      name: "vault",
      to: "/vault/main",
      label: t("bottomNav.vault"),
      icon: VaultIcon,
      badge: vaultBadgeCount,
      badgeOffsets: BADGE_OFFSETS.vault,
      iconSize: NAV_ICON_SIZE,
    },
    {
      name: "events",
      to: "/events/search",
      itemState: eventsNavigationState,
      label: t("bottomNav.events"),
      icon: EventsIcon,
      activeIcon: PlusNavIcon,
      badge: hostEventAttentionTotal,
      badgeOffsets: BADGE_OFFSETS.events,
      iconSize: NAV_ICON_SIZE,
      activeIconSize: 28,
    },
    {
      name: "clubs",
      to: "/clubs",
      label: t("bottomNav.clubs"),
      icon: ClubsIcon,
      asset: CLUBS_NAV_WORDMARK_SRC,
      activeAsset: CLUBS_NAV_WORDMARK_SRC,
      assetClassName: "h-[24px] w-[24px] shrink-0",
      activeAssetClassName: "h-[24px] w-[24px] shrink-0",
      badge: unreadClubMembers + pendingClubRequests + unreadJoinedClubs,
      badgeOffsets: BADGE_OFFSETS.clubs,
      iconSize: CLUBS_NAV_ICON_SIZE,
    },
    {
      name: "chats",
      to: "/menu/chats",
      label: t("bottomNav.chats", "Chats"),
      asset: FIGMA_NAV_ASSETS.chatsIcon,
      badge: chatsAttentionTotal,
      badgeOffsets: BADGE_OFFSETS.menu,
      iconSize: 19,
    },
  ];

  const moreTabs = [
    {
      name: "shop",
      to: "/shop/garment",
      label: t("bottomNav.shop"),
      asset: `${MENU_ASSET_BASE}/shop_icon.svg`,
      iconClassName: "h-[22px] w-[20px]",
    },
    {
      name: "singles",
      to: "/menu/singles",
      label: t("bottomNav.singles", "Singles"),
      asset: `${MENU_ASSET_BASE}/singles_icon.svg`,
      badge: unreadSingles,
      badgeOffsets: BADGE_OFFSETS.menu,
      iconClassName: "h-[24px] w-[24px]",
    },
    {
      name: "dinners",
      to: "/menu/dinners",
      label: t("bottomNav.dinners", "Dinners"),
      asset: `${MENU_ASSET_BASE}/dinners_icon.svg`,
      badge: unreadDinners,
      badgeOffsets: BADGE_OFFSETS.menu,
      iconClassName: "h-[21px] w-[24px]",
    },
    {
      name: "search",
      to: "/search/main",
      label: t("bottomNav.search"),
      asset: `${MENU_ASSET_BASE}/search_icon.svg`,
      badge: unreadStatistics,
      badgeOffsets: BADGE_OFFSETS.search,
      iconClassName: "h-[24px] w-[24px]",
    },
  ];

  const renderPrimaryButton = ({
    name,
    to,
    itemState,
    label,
    icon: Icon,
    activeIcon: ActiveIcon,
    activeText,
    activeAsset,
    activeAssetClassName,
    asset,
    assetClassName,
    iconSize,
    activeIconSize,
    badge = 0,
    badgeOffsets,
  }) => {
    const isActive = activeTab === name;
    const showBadge = !(name === "events" && isActive);
    const DisplayIcon = isActive && ActiveIcon ? ActiveIcon : Icon;
    const displayIconSize = isActive && ActiveIcon ? activeIconSize || iconSize : iconSize;
    const iconColor = "#FFFFFF";
    const handleClick = (e) => {
      guardClick(e, name);
      if (!e.defaultPrevented && name === "events" && isActive) {
        e.preventDefault();
        const createAction = new CustomEvent("vg:events-create-action", { cancelable: true });
        window.dispatchEvent(createAction);
        if (createAction.defaultPrevented) return;
        navigate("/events/create");
      }
    };

    return (
      <Link
        key={name}
        to={to}
        state={itemState}
        aria-label={label}
        title={label}
        onClick={handleClick}
        className="relative z-[2] flex h-[61px] w-[61px] shrink-0 items-center justify-center rounded-full text-white no-underline"
      >
        <span
          className={[
            "relative flex h-[61px] w-[61px] items-center justify-center rounded-full",
            isActive
              ? "bg-[radial-gradient(circle_at_42%_30%,#6EA7FF_0%,#4C8EFF_56%,#2F77F3_100%)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)]"
              : "ring-1 ring-inset ring-white/25",
          ].join(" ")}
        >
          {!isGuest && showBadge ? renderUnreadBadge(badge, badgeOffsets) : null}
          {isActive && activeAsset ? (
            <img
              src={activeAsset}
              alt=""
              className={`relative z-[2] object-contain ${activeAssetClassName || "h-5 w-5"}`}
            />
          ) : isActive && activeText ? (
            <span className="relative z-[2] flex max-w-[52px] items-center justify-center text-center text-[11px] font-black uppercase leading-none text-white">
              {activeText}
            </span>
          ) : asset ? (
            <img
              src={asset}
              alt=""
              className={`relative z-[2] object-contain ${assetClassName || "h-[19px] w-[19px]"}`}
            />
          ) : (
            <DisplayIcon
              width={displayIconSize}
              height={displayIconSize}
              style={{ color: iconColor }}
            />
          )}
        </span>
      </Link>
    );
  };

  const renderMoreButton = ({
    name,
    to,
    label,
    asset,
    iconClassName,
    badge = 0,
    badgeOffsets,
  }) => {
    const isActive = activeTab === name;

    return (
      <Link
        key={name}
        to={to}
        aria-label={label}
        title={label}
        onClick={(e) => guardClick(e, name)}
        className="relative z-[2] flex h-[61px] w-[61px] shrink-0 items-center justify-center rounded-full no-underline"
      >
        <img
          src={FIGMA_NAV_ASSETS.moreRing}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full"
        />
        {isActive ? (
          <span className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_42%_30%,#6EA7FF_0%,#4C8EFF_56%,#2F77F3_100%)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)]" />
        ) : null}
        {!isGuest && renderUnreadBadge(badge, badgeOffsets)}
        <img src={asset} alt="" className={`relative z-[2] ${iconClassName}`} />
      </Link>
    );
  };

  const pageGutterPx = viewportWidth
    ? Math.min(
        NAV_MAX_PAGE_GUTTER_PX,
        Math.max(NAV_MIN_PAGE_GUTTER_PX, viewportWidth * NAV_PAGE_GUTTER_VW)
      )
    : 0;
  const availableNavWidthPx = viewportWidth
    ? Math.min(
        NAV_MAX_WIDTH_PX,
        Math.max(0, viewportWidth - pageGutterPx * 2)
      )
    : 0;
  const navScale = availableNavWidthPx
    ? Math.min(1, availableNavWidthPx / NAV_BASE_WIDTH_PX)
    : 1;
  const navLayoutWidthPx = availableNavWidthPx
    ? Math.max(NAV_BASE_WIDTH_PX, availableNavWidthPx)
    : 0;
  const navOuterStyle = availableNavWidthPx
    ? {
        width: `${availableNavWidthPx}px`,
        height: `${NAV_HEIGHT_PX * navScale}px`,
      }
    : { width: NAV_WIDTH_CSS, height: `${NAV_HEIGHT_PX}px` };
  const navInnerStyle = availableNavWidthPx
    ? {
        width: `${navLayoutWidthPx}px`,
        transform: `translateX(-50%) scale(${navScale})`,
        transformOrigin: "bottom center",
      }
    : { width: NAV_WIDTH_CSS };

  return (
    <nav
      ref={navRef}
      id="bottom-nav"
      className={`fixed inset-x-0 z-[90] m-0 w-full bg-transparent p-0 ${className}`}
      style={{
        ...(chatInputViewportStyle || nativeIosChatRestingStyle || {
          bottom: `max(env(safe-area-inset-bottom, 0px), ${NAV_BOTTOM_GAP_PX}px)`,
        }),
        ...(nativeIosChatOverlay
          ? { transition: `transform ${CHAT_KEYBOARD_TRANSITION}` }
          : {}),
      }}
      data-chat-keyboard-open={chatVisualViewport.keyboardOpen ? "true" : "false"}
      data-chat-input-visible={showChatInputSlot ? "true" : "false"}
    >
      {showScrim && !bottomNavSuppressed && !showChatInputSlot ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[170px] bg-[linear-gradient(0deg,rgba(0,0,0,0.62)_0%,rgba(0,0,0,0.5)_28%,rgba(0,0,0,0.22)_62%,rgba(0,0,0,0)_100%)]"
        />
      ) : null}
      <button
        type="button"
        aria-label={t("bottomNav.closeMore", "Close menu")}
        tabIndex={isMoreOpen ? 0 : -1}
        onClick={() => setIsMoreOpen(false)}
        className={[
          "fixed inset-0 z-0 m-0 border-0 bg-black/[0.68] p-0 transition-opacity duration-300 ease-out",
          isMoreOpen && !bottomNavSuppressed && !showChatInputSlot ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
        ].join(" ")}
      />

      {/* Chat input replaces BottomNavigation while a chat is open. */}
      {showChatInputSlot ? (
        <div
          id="chat-input-slot"
          className="relative z-[3] mx-auto w-full"
          style={{ "--bottom-nav-height": "0px" }}
        >
          <ChatInputBar
            // Use nav i18n by default; Chat may also pass its own `t`
            t={chatInputSlot?.props?.t || t}
            {...(chatInputSlot.props || {})}
            embedded
          />
        </div>
      ) : null}

      {singlesDiscoverActionsState.visible && !bottomNavSuppressed && !showChatInputSlot && !isMoreOpen ? (
        <div
          id="singles-discover-actions"
          data-bottom-clearance-gap="10"
          className="absolute inset-x-0 -top-[7.55rem] z-[4] flex justify-center pointer-events-none lg:hidden"
        >
          <div className="pointer-events-auto flex items-center justify-center gap-3">
            <div className="flex h-[5.7rem] w-[5.7rem] items-center justify-center">
              <SinglesDiscoverActionButton
                id="singles-discover-dislike-nav"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("vg-singles-discover-action", { detail: { action: "dislike" } })
                  )
                }
                disabled={singlesDiscoverActionsState.swipeDisabled}
              >
                <img
                  src={SINGLES_DISCOVER_DISLIKE_SRC}
                  alt=""
                  className="block h-[1.78rem] w-[1.78rem] object-contain"
                />
              </SinglesDiscoverActionButton>
            </div>
            <div className="flex h-[7.2rem] w-[7.2rem] items-center justify-center">
              <SinglesDiscoverActionButton
                id="singles-discover-like-nav"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("vg-singles-discover-action", { detail: { action: "like" } })
                  )
                }
                large
                active
                disabled={singlesDiscoverActionsState.swipeDisabled}
              >
                <img
                  src={SINGLES_DISCOVER_BIG_HEART_SRC}
                  alt=""
                  className="h-[8.35rem] w-[8.35rem] max-w-none object-contain"
                  style={{ transform: `translateY(${SINGLES_DISCOVER_HEART_OFFSET_Y_REM}rem)` }}
                />
              </SinglesDiscoverActionButton>
            </div>
            <div className="flex h-[5.7rem] w-[5.7rem] items-center justify-center">
              <div className="relative flex h-[5.7rem] w-[5.7rem] items-center justify-center">
                <SinglesDiscoverActionButton
                  id="singles-discover-superlike-nav"
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent("vg-singles-discover-action", { detail: { action: "superlike" } })
                    )
                  }
                  disabled={singlesDiscoverActionsState.superLikeDisabled}
                >
                  <img
                    src={SINGLES_DISCOVER_STAR_SRC}
                    alt=""
                    className="block h-[1.88rem] w-[1.88rem] object-contain"
                  />
                </SinglesDiscoverActionButton>
                {singlesDiscoverActionsState.showSuperLikeCounter ? (
                  <SinglesDiscoverSuperLikeCounter
                    remaining={singlesDiscoverActionsState.superLikesRemaining}
                    total={5}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!bottomNavSuppressed && !showChatInputSlot ? (
        <div className="relative z-[2] mx-auto" style={navOuterStyle}>
          <div className="absolute bottom-0 left-1/2 h-[70px]" style={navInnerStyle}>
            {isMorePanelRendered && !showChatInputSlot ? (
              <div
                id="bottom-nav-more-panel"
                aria-hidden={!isMoreOpen}
                className="absolute bottom-[80px] z-[5] h-[90px] overflow-hidden rounded-[32px] transform-gpu transition-[opacity,transform] duration-200 ease-out will-change-[opacity,transform]"
                style={{
                  ...navSectionSurfaceStyle,
                  right: "75px",
                  width: "calc(100% - 75px)",
                  opacity: isMorePanelVisible ? 1 : 0,
                  transform: isMorePanelVisible
                    ? "translate3d(0,0,0) scale(1)"
                    : "translate3d(0,10px,0) scale(0.96)",
                  transformOrigin: "bottom right",
                  pointerEvents: isMorePanelVisible ? "auto" : "none",
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                  contain: "layout paint style",
                }}
              >
                <div className="absolute inset-x-0 top-[5px] z-[3] flex h-[61px] items-center justify-between px-[5px]">
                  {moreTabs.map((item) => renderMoreButton(item))}
                </div>
                <div className="absolute inset-x-0 top-[65px] z-[3] flex h-[22px] items-center justify-between px-[5px]">
                  {moreTabs.map((item) => (
                    <span
                      key={`${item.name}-label`}
                      className="flex h-[22px] w-[61px] items-center justify-center text-center text-[9px] font-normal leading-[22px] text-white"
                    >
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {showParticipantRulesButton ? <FloatingParticipantRulesButton /> : null}
            {showSubscriptionButton ? <FloatingSubscribeButton /> : null}

            <div
              className="pointer-events-none absolute left-0 top-0 h-[70px] overflow-hidden rounded-full"
              style={{ ...navSectionSurfaceStyle, width: "calc(100% - 75px)" }}
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute right-0 top-0 h-[70px] w-[70px] overflow-hidden rounded-full"
              style={navSectionSurfaceStyle}
              aria-hidden="true"
            />
            <div
              className="absolute left-0 top-[5px] z-[3] flex h-[61px] items-center justify-between px-[5px]"
              style={{ width: "calc(100% - 75px)" }}
            >
              {primaryTabs.map((item) => renderPrimaryButton(item))}
            </div>

            <button
              type="button"
              aria-label={t("bottomNav.more", "More")}
              aria-expanded={isMoreOpen}
              onClick={() => setIsMoreOpen((open) => !open)}
              className="absolute right-0 top-0 flex h-[70px] w-[70px] items-center justify-center rounded-full bg-transparent text-white"
            >
              <span
                className={[
                  "relative flex h-[61px] w-[61px] items-center justify-center rounded-full",
                  isMoreOpen || isMoreTabActive
                    ? "bg-[radial-gradient(circle_at_42%_30%,#6EA7FF_0%,#4C8EFF_56%,#2F77F3_100%)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)]"
                    : "ring-1 ring-inset ring-white/25",
                ].join(" ")}
              >
                {isMoreOpen ? (
                  <img
                    src={FIGMA_NAV_ASSETS.closeIcon}
                    alt=""
                    className="h-[13px] w-[13px]"
                  />
                ) : (
                  <>
                    {!isGuest ? renderUnreadBadge(unreadCompass, BADGE_OFFSETS.compass) : null}
                    <img
                      src={FIGMA_NAV_ASSETS.compassIcon}
                      alt=""
                      className="h-[22px] w-[22px]"
                    />
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      ) : null}
    </nav>
  );
};

export default BottomNavigation;
